// ----------------------------------------------------------------------
// <copyright file="Izenda.Validator.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  require('share/utils/Izenda.StringUtilities');
  var React = require('react');
  var m = require('mobx');
  var TooltipUtils = require('share/utils/Izenda.TooltipUtils');

  var classnames = require('classnames');
  var observer = require('mobx-react').observer;

  const { get, isArray, join, omit, assign, isEmpty, filter, flow, map } = require('lodash/fp');

  /**
   * Return whether the validator state has changed
   * @param  {Object}  currState Current validator state
   * @param  {Object}  prevState Previous validator state
   */
  function isValidationStateChanged(currState, prevState) {
    return (
      currState.isPristine !== prevState.isPristine ||
      currState.error !== prevState.error ||
      currState.messages.length !== prevState.messages.length ||
      currState.messages[0] !== prevState.messages[0]
    );
  }

  /**
   * A React higher order compoment that enhances the validation to target component
   * @param  {Object}  options
   * @return {Function}       A function that accept a target component.
   */
  function Validator(options) {
    return function(EditComponent) {
      var ValidatableEditor = React.createClass({
        hasTooltip: false,
        displayName: 'Validator(' + EditComponent.displayName + ')',
        contextTypes: {
          validation: React.PropTypes.object.isRequired
        },
        propTypes: {
          field: React.PropTypes.string.isRequired,
          validations: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.object]),
          group: React.PropTypes.oneOfType([React.PropTypes.string, React.PropTypes.number]),
          onValidated: React.PropTypes.func,
          onReset: React.PropTypes.func,
          shouldInjectStateToChildren: React.PropTypes.bool
        },
        getDefaultProps: function() {
          return {
            field: 'ValidatableEditor',
            shouldInjectStateToChildren: false,
            tooltipPosition: get('tooltipPosition', options) || 'top'
          };
        },
        getInitialState: function() {
          return {
            isPristine: true, // Indicate fresh state without change and unvalidated.
            isUnvalidatedChange: false, // Indicate value has been changed but unvalidated
            error: false, // Indicate validated failed
            messages: [] // Validation error messages
          };
        },

        componentDidMount: function() {
          this.context.validation.attachComponent(this);

          let title = this.state.messages[0];
          if (isArray(this.state.messages[0]) && this.state.messages[0].length > 1) {
            title = flow(map(m => '- ' + m), join('\n'))(this.state.messages[0]);
          }

          const tooltipOption = {
            title: title,
            trigger: 'hover',
            placement: 'auto ' + this.props.tooltipPosition,
            template:
              '<div class="tooltip" role="tooltip"><div class="tooltip-arrow bg-errors"></div><div class="tooltip-inner bg-errors"></div></div>'
          };
          TooltipUtils.initTooltip(this.refs.editor, tooltipOption);
          TooltipUtils.disableTooltip(this.refs.editor);
        },

        componentDidUpdate: function(prevProps, prevState) {
          if (isValidationStateChanged(this.state, prevState)) {
            if (!this.state.isPristine && this.state.error) {
              let title = this.state.messages[0];
              if (isArray(this.state.messages[0]) && this.state.messages[0].length > 1) {
                title = flow(map(mess => '- ' + mess), join('\n'))(this.state.messages[0]);
              }
              TooltipUtils.updateTooltip(this.refs.editor, title);
            } else {
              TooltipUtils.disableTooltip(this.refs.editor);
            }
          }
        },

        componentWillUnmount: function() {
          this.context.validation.detachComponent(this);
          TooltipUtils.destroyTooltip(this.refs.editor);
        },

        render: function() {
          var onChange = this.props.onChange;
          var shouldInjectStateToChildren = this.props.shouldInjectStateToChildren;

          var wrappedOnChange = function() {
            !this.state.isPristine && this.setUnvalidatedChange();
            if (onChange) {
              return onChange.apply(null, arguments);
            }
            return null;
          }.bind(this);

          var rest = omit(
            [
              'field',
              'group',
              'validations',
              'className',
              'onChange',
              'onValidated',
              'onReset',
              'shouldInjectStateToChildren',
              'tooltipPosition'
            ],
            this.props
          );

          if (shouldInjectStateToChildren) {
            rest = assign(
              {
                validateInfo: this.state
              },
              rest
            );
          }

          var classes = classnames(this.props.className, 'validator ', {
            unvalidated: !this.state.isPristine && this.state.isUnvalidatedChange,
            errors: !this.state.isPristine && !this.state.isUnvalidatedChange && this.state.error,
            success: !this.state.isPristine && !this.state.isUnvalidatedChange && !this.state.error
          });

          var key = this.context.validation.getKey(this);

          return (
            <div ref="container" id={String.hashKeyId(key)} className={this.props.containerClassName}>
              <EditComponent
                className={classes}
                {...rest}
                onChange={wrappedOnChange}
                ref="editor"
                data-toggle="tooltip"
              />
            </div>
          );
        },

        /**
         * Get component value that will be used to validate
         */
        getValue: function() {
          return options && options.getValue ? options.getValue(this.refs.editor) : this.refs.editor.props.value;
        },

        /**
         * Set pristine state of validator. By calling this, the validator
         * will reset its state.
         */
        setPristine: function() {
          this.props.onReset && this.props.onReset();
          this.isMounted() &&
            this.setState({
              isPristine: true,
              isUnvalidatedChange: false,
              error: false,
              messages: []
            });
        },

        /**
         * Set error state including its error messages. The validator will show
         * error indicator and error messages.
         */
        setError: function(msgs) {
          this.props.onValidated && this.props.onValidated(false);
          this.isMounted() &&
            this.setState({
              isPristine: false,
              isUnvalidatedChange: false,
              error: true,
              messages: this.getErrorMessages(m.toJS(msgs))
            });
        },

        /**
         * Get messages from list errors
         */
        getErrorMessages: function(errorObject) {
          var result = [];

          var clientErrorMessages = filter(function(o) {
            return typeof o === 'string';
          }, errorObject);
          if (clientErrorMessages.length > 0) return clientErrorMessages;

          errorObject.map(function(error) {
            error.messages && result.push(error.messages);
          });
          return isEmpty(result) ? null : result;
        },

        /**
         * Set success state. The validator will show success indicator.
         */
        setSuccess: function() {
          this.props.onValidated && this.props.onValidated(true);
          this.isMounted() &&
            this.setState({
              isPristine: false,
              isUnvalidatedChange: false,
              error: false,
              messages: []
            });
        },
        /**
         * Set unvalidated changed state
         */
        setUnvalidatedChange: function() {
          this.isMounted() &&
            this.setState({
              isPristine: false,
              isUnvalidatedChange: true,
              error: false,
              messages: []
            });
        }
      });
      return observer(ValidatableEditor);
    };
  }

  module.exports = Validator;
})();



// WEBPACK FOOTER //
// ./ui/share/components/hoc/Izenda.Validator.jsx